import { Component, ViewChild, ElementRef, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { catchError, debounceTime, distinctUntilChanged, forkJoin, map, merge, of, startWith, switchMap, throwError } from 'rxjs';
import { EmailHubService } from 'src/app/private/services/email-hub.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';

@Component({
  selector: 'app-mail-middle-section',
  templateUrl: './mail-middle-section.component.html',
  styleUrls: ['./mail-middle-section.component.scss'],
})
export class MailMiddleSectionComponent implements OnChanges {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  @Input() activeAction: string = 'inbox';
  @Input() mailProvider: 'outlook' | 'gmail' = 'outlook';
  @Output() selecteRow = new EventEmitter<any>();
  @Input() isAccountNotConnected = false;
  @Output() changeAccountNotConnected = new EventEmitter<boolean>();
  searchQuery = new FormControl();
  top = 10;
  skipToken: any = '';
  skip = 0;
  loadingData = false;

  loading = true;

  dataSource: any[] = [];

  selectedRow: any;

  toasters: any;

  selectedIds: any[] = [];

  btnLoading = false;

  hoverelmentId: any = null;
  loadMore = false;
  currentLimit = 10;
  maxLimit = 10;
  currentUser: any;

  constructor(
    private api: EmailHubService,
    private toast: ToastrService,
    private localStorage: LocalStorageService,
    private modalService: NgbModal

  ) { }


  ngOnInit() {
    this.loadData();
    this.currentUser = this.localStorage.getProfileName()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mailProvider'] && !changes['mailProvider'].firstChange) {
      this.skipToken = '';
      this.skip = 0;
      this.dataSource = [];
      this.loadData();
    }
  }

  private parseAddressHeader(h: string): { name: string; address: string } {
    if (!h) return { name: 'Unknown', address: '' };
    const angle = h.match(/<([^>]+)>/);
    const addr = angle ? angle[1].trim() : h.replace(/"/g, '').trim();
    let name = h.replace(/<[^>]+>/, '').replace(/"/g, '').trim();
    if (!name) name = (addr.split('@')[0] || 'Unknown');
    return { name, address: addr };
  }

  private headerToRecipients(header: string): any[] {
    if (!header) return [];
    return header.split(',').map(part => {
      const p = this.parseAddressHeader(part.trim());
      return { emailAddress: { name: p.name, address: p.address } };
    }).filter(x => x.emailAddress.address);
  }

  /** Gmail snippets often include HTML entities (e.g. &#39;) — decode for list preview. */
  private decodeSnippetEntities(snippet: string): string {
    if (!snippet) return '';
    if (typeof document === 'undefined') return snippet;
    const ta = document.createElement('textarea');
    ta.innerHTML = snippet;
    return ta.value;
  }

  /** Map Gmail API row to Outlook-like shape for shared templates */
  mapGmailRow(m: any): any {
    const fromParsed = this.parseAddressHeader(m.from);
    return {
      id: m.id,
      threadId: m.threadId,
      subject: m.subject,
      bodyPreview: this.decodeSnippetEntities(m.snippet || ''),
      receivedDateTime: m.date ? new Date(m.date).toISOString() : new Date().toISOString(),
      sender: { emailAddress: { name: fromParsed.name, address: fromParsed.address } },
      from: { emailAddress: { name: fromParsed.name, address: fromParsed.address } },
      toRecipients: this.headerToRecipients(m.to),
      ccRecipients: this.headerToRecipients(m.cc),
      gmailDraftId: m.draftId,
    };
  }

  getSkipToken(url: any) {
    const params = new URL(url).searchParams; // Parse the query parameters
    return params.get('$skiptoken'); // Extract the skiptoken parameter
  }

  /** Gmail list endpoints expect `searchKeyword` → backend maps to Gmail `messages.list` `q`. */
  private buildGmailListBody(extra?: { pageToken?: string; maxResults?: number }): Record<string, unknown> {
    const kw = String(this.searchQuery.value ?? '').trim();
    const pageToken = extra?.pageToken;
    return {
      client_id: this.localStorage.getClientId(),
      user_email: this.localStorage.getMailHubUserEmail(),
      maxResults: extra?.maxResults ?? this.top,
      ...(pageToken ? { pageToken } : {}),
      ...(kw ? { searchKeyword: kw } : {})
    };
  }

  loadData() {

    console.log("activeAction", this.activeAction);

    try {
      merge(
        this.searchQuery.valueChanges.pipe(
          debounceTime(1000), // Wait for 300ms after the last keystroke
          distinctUntilChanged() // Only trigger if the value has changed

        )
      )
        .pipe(
          startWith({}),
          switchMap(() => {

            if (this.searchQuery.value === '') {
              this.skipToken = '';
            }

            if (this.searchQuery.value || this.skipToken) {
              this.skip = 0;
            }


            const param = {
              client_id: this.localStorage.getClientId(),
              user_email: this.localStorage.getMailHubUserEmail(),
              searchKeyword: this.searchQuery.value,
              requestType: this.searchQuery.value ? 'search' : 'normal',
              top: this.top,
              skip: 0,
              skiptoken: this.skip > 0 ? this.skipToken : ''
            };

            const gparam = this.buildGmailListBody({ pageToken: this.skipToken || undefined });

            this.loadingData = true;

            if (this.mailProvider === 'gmail') {
              if (this.activeAction === 'inbox') {
                return this.api.getGmailInboxEmail(gparam).pipe(catchError((e) => this.handleGmailError(e)));
              }
              if (this.activeAction === 'sent') {
                return this.api.getGmailSentEmail(gparam).pipe(catchError((e) => this.handleGmailError(e)));
              }
              if (this.activeAction === 'drafts') {
                return this.api.getGmailDraftEmail(gparam).pipe(catchError((e) => this.handleGmailError(e)));
              }
              if (this.activeAction === 'trash') {
                return this.api.getGmailSpamEmail(gparam).pipe(catchError((e) => this.handleGmailError(e)));
              }
              return this.api.getGmailTrashEmail(gparam).pipe(catchError((e) => this.handleGmailError(e)));
            }

            if (this.activeAction === 'inbox') {
              return this.api.getOutlookInboxEmail(param).pipe(catchError((e) => this.handleOutlookError(e)));
            }
            if (this.activeAction === 'sent') {
              return this.api.getOutlookSentEmail(param).pipe(catchError((e) => this.handleOutlookError(e)));
            }
            if (this.activeAction === 'drafts') {
              return this.api.getOutlookDraftEmail(param).pipe(catchError((e) => this.handleOutlookError(e)));
            }
            if (this.activeAction === 'trash') {
              return this.api.getOutlookTrashEmail(param).pipe(catchError((e) => this.handleOutlookError(e)));
            }
            return this.api.getOutlookDeletedEmail(param).pipe(catchError((e) => this.handleOutlookError(e)));
          }),
          map((response: any) => {
            this.loading = false;

            if (this.mailProvider === 'gmail') {
              if (response?.message === 'Please Link your Gmail Mailbox') {
                this.changeAccountNotConnected.emit(true);
                return [];
              }
              this.changeAccountNotConnected.emit(false);
              this.skipToken = response?.nextPageToken || '';
              this.skip = this.top;
              const raw = this.activeAction === 'drafts' ? (response?.drafts || []) : (response?.messages || []);
              const mapped = (raw || []).map((row: any) => this.mapGmailRow(row));
              this.maxLimit = response?.nextPageToken ? 999999 : mapped.length;
              return mapped;
            }

            if (response?.statusCode === 200) {
              this.changeAccountNotConnected.emit(true);
              return [];
            }
            this.changeAccountNotConnected.emit(false);

            if (response?.value === null) {
              return [];
            }
            this.skipToken = response['@odata.nextLink'];

            if (this.searchQuery.value && this.skipToken) {
              const skipToken = this.getSkipToken(this.skipToken);
              this.skipToken = skipToken;
              this.skip = 0;
            } else {
              this.skipToken = '';
              this.skip = this.top;
            }

            if (this.activeAction === 'inbox') {
              this.maxLimit = response?.totalInboxEmailCount;
            } else if (this.activeAction === 'sent') {
              this.maxLimit = response?.totalSentEmailCount;
            } else if (this.activeAction === 'drafts') {
              this.maxLimit = response?.totalDraftEmailCount;
            } else if (this.activeAction === 'trash') {
              this.maxLimit = response?.totalJunkEmailCount;
            } else if (this.activeAction === 'deleted') {
              this.maxLimit = response?.totalDeletedEmailCount;
            }

            return response?.value;
          })
        )
        .subscribe({
          next: (data) => {
            this.loadingData = false;
            this.dataSource = data;

          },
          error: (e) => {
            this.loading = false;
            this.loadingData = false;
            console.error(e);
          },
          complete: () => {
            this.loading = false;
          },
        });
    } catch (e) {
      this.loading = false;
      console.error(e);
    }
  }

  loadDataMore() {
    try {
      merge()
        .pipe(
          startWith({}),
          switchMap(() => {
            const param = {
              client_id: this.localStorage.getClientId(),
              user_email: this.localStorage.getMailHubUserEmail(),
              searchKeyword: this.searchQuery.value,
              requestType: this.searchQuery.value ? 'search' : 'normal',
              top: this.top,
              skip: this.skip,
              skiptoken: this.skipToken
            };

            const gparam = this.buildGmailListBody({ pageToken: this.skipToken || undefined });

            this.loadMore = true;

            if (this.mailProvider === 'gmail') {
              if (!this.skipToken) {
                return of(null);
              }
              if (this.activeAction === 'inbox') {
                return this.api.getGmailInboxEmail(gparam).pipe(catchError(() => of(null)));
              }
              if (this.activeAction === 'sent') {
                return this.api.getGmailSentEmail(gparam).pipe(catchError(() => of(null)));
              }
              if (this.activeAction === 'drafts') {
                return this.api.getGmailDraftEmail(gparam).pipe(catchError(() => of(null)));
              }
              if (this.activeAction === 'trash') {
                return this.api.getGmailSpamEmail(gparam).pipe(catchError(() => of(null)));
              }
              return this.api.getGmailTrashEmail(gparam).pipe(catchError(() => of(null)));
            }

            if (this.activeAction === 'inbox') {
              return this.api.getOutlookInboxEmail(param).pipe(catchError(() => of(null)));
            }
            if (this.activeAction === 'sent') {
              return this.api.getOutlookSentEmail(param).pipe(catchError(() => of(null)));
            }
            if (this.activeAction === 'drafts') {
              return this.api.getOutlookDraftEmail(param).pipe(catchError(() => of(null)));
            }
            if (this.activeAction === 'trash') {
              return this.api.getOutlookTrashEmail(param).pipe(catchError(() => of(null)));
            }
            return this.api.getOutlookDeletedEmail(param).pipe(catchError(() => of(null)));
          }),
          map((response: any) => {
            this.loadMore = false;
            if (response == null) {
              return [];
            }
            if (this.mailProvider === 'gmail') {
              this.skipToken = response?.nextPageToken || '';
              const raw = this.activeAction === 'drafts' ? (response?.drafts || []) : (response?.messages || []);
              return (raw || []).map((row: any) => this.mapGmailRow(row));
            }
            if (response?.value === null) {
              return [];
            }
            this.skipToken = response['@odata.nextLink'];
            if (this.searchQuery.value && this.skipToken) {
              const skipToken = this.getSkipToken(this.skipToken);
              this.skipToken = skipToken;
            } else {
              this.skipToken = '';
            }
            return response?.value;
          })
        )
        .subscribe({
          next: (data) => {
            this.loadingData = false;
            this.loadMore = false;
            const chunk = data || [];
            this.dataSource = [...this.dataSource, ...chunk];
            this.skip = this.skip + this.top;
            if (this.mailProvider === 'gmail' && !this.skipToken) {
              this.maxLimit = this.dataSource.length;
            }
          },
          error: (e) => {
            this.loading = false;
            this.loadingData = false;
            this.loadMore = false;
            console.error(e);
          },
          complete: () => {
            this.loadMore = false;
            this.loading = false;
          },
        });
    } catch (e) {
      this.loadMore = false;
      this.loading = false;
      console.error(e);
    }
  }

  refetch() {


    try {
      merge()
        .pipe(
          startWith({}),
          switchMap(() => {
            // console.log("client_id", this.localStorage.getClientId())
            // this.toasters = this.toast.warning('Loading...', '', {
            //   disableTimeOut: true, // Keep the toast open indefinitely
            // });

            this.loadingData = true;
            const param = {
              client_id: this.localStorage.getClientId(),
              user_email: this.localStorage.getMailHubUserEmail(),
              searchKeyword: this.searchQuery.value,
              requestType: this.searchQuery.value ? 'search' : 'normal',
              top: this.top,
              skip: 0,
              skiptoken: this.skipToken
            };

            const gparam = this.buildGmailListBody();

            if (this.mailProvider === 'gmail') {
              if (this.activeAction === 'inbox') {
                return this.api.getGmailInboxEmail(gparam).pipe(catchError(() => of(null)));
              }
              if (this.activeAction === 'sent') {
                return this.api.getGmailSentEmail(gparam).pipe(catchError(() => of(null)));
              }
              if (this.activeAction === 'drafts') {
                return this.api.getGmailDraftEmail(gparam).pipe(catchError(() => of(null)));
              }
              if (this.activeAction === 'trash') {
                return this.api.getGmailSpamEmail(gparam).pipe(catchError(() => of(null)));
              }
              return this.api.getGmailTrashEmail(gparam).pipe(catchError(() => of(null)));
            }

            if (this.activeAction === 'inbox') {
              return this.api.getOutlookInboxEmail(param).pipe(catchError(() => of(null)));
            }
            if (this.activeAction === 'sent') {
              return this.api.getOutlookSentEmail(param).pipe(catchError(() => of(null)));
            }
            if (this.activeAction === 'drafts') {
              return this.api.getOutlookDraftEmail(param).pipe(catchError(() => of(null)));
            }
            if (this.activeAction === 'trash') {
              return this.api.getOutlookTrashEmail(param).pipe(catchError(() => of(null)));
            }
            return this.api.getOutlookDeletedEmail(param).pipe(catchError(() => of(null)));
          }),
          map((response: any) => {
            this.loading = false;
            if (response == null) {
              return [];
            }
            if (this.mailProvider === 'gmail') {
              this.skipToken = response?.nextPageToken || '';
              this.skip = this.top;
              const raw = this.activeAction === 'drafts' ? (response?.drafts || []) : (response?.messages || []);
              const mapped = (raw || []).map((row: any) => this.mapGmailRow(row));
              this.maxLimit = response?.nextPageToken ? 999999 : mapped.length;
              return mapped;
            }
            if (response?.value === null) {
              return [];
            }
            return response?.value;
          })
        )
        .subscribe({
          next: (data) => {
            this.dataSource = data;
            this.skip = this.top;
            this.toast.clear(this.toasters.toastId);
            this.loadingData = false;

          },
          error: (e) => {
            this.loading = false;
            this.loadingData = false;

            console.error(e);
          },
          complete: () => {
            this.loadingData = false;
            this.loading = false;
          },
        });
    } catch (e) {
      this.loading = false;
      this.loadingData = false;

      console.error(e);
    }
  }



  onScroll(container: HTMLElement) {
    console.log('scrolling');
    const threshold = 300; // Adjust based on your needs
    const { scrollTop, scrollHeight, clientHeight } = container;

    if (scrollTop + clientHeight >= scrollHeight - threshold && !this.loading && !this.loadingData && !this.loadMore

      && this.dataSource.length < this.maxLimit
    ) {
      this.loadDataMore();
    }
  }

  setSeelctRow(row: any) {
    this.selectedRow = row;
    this.selecteRow.emit(row);
  }

  updateSelection(element: any): void {
    console.log('Element:', element);

    const index = this.selectedIds.indexOf(element.id);

    if (index === -1) {
      this.selectedIds.push(element.id);
    } else {
      this.selectedIds.splice(index, 1);
    }

    console.log('Selected IDs:', this.selectedIds);
  }

  openDeleteGroup(content: any) {
    this.modalService
      .open(content, { size: 'md', scrollable: true, centered: true })
  }

  openDeleteGroup2(content: any, id: any) {
    this.selectedIds = [id];
    this.modalService
      .open(content, { size: 'md', scrollable: true, centered: true })
  }

  handleDeleteEvent() {
    this.btnLoading = true;
    const clientId = this.localStorage.getClientId();
    const userEmail = this.localStorage.getMailHubUserEmail();

    if (this.mailProvider === 'gmail') {
      const reqs = this.selectedIds.map((id) =>
        this.activeAction === 'deleted'
          ? this.api.deleteGmailEmailPermanently({
              client_id: clientId,
              user_email: userEmail,
              message_id: id
            })
          : this.api.deleteGmailEmail({
              client_id: clientId,
              user_email: userEmail,
              message_id: id
            })
      );
      forkJoin(reqs.length ? reqs : [of(null)]).subscribe({
        next: () => {
          setTimeout(() => {
            this.btnLoading = false;
            this.modalService.dismissAll();
            this.toast.success('Email deleted successfully');
          }, 500);
          this.refetch();
          this.selectedIds = [];
        },
        error: (e) => {
          console.error(e);
          const apiMsg = e?.error?.message;
          this.toast.error(
            typeof apiMsg === 'string' && apiMsg.trim() ? apiMsg : 'Error while deleting email'
          );
          this.btnLoading = false;
        }
      });
      return;
    }

    const param = {
      client_id: clientId,
      user: userEmail,
      messageids: this.selectedIds
    };

    if (this.activeAction === 'deleted') {
      this.api.deleteOutlookEmailPermanently(param).subscribe({
        next: () => {
          setTimeout(() => {
            this.btnLoading = false;
            this.modalService.dismissAll();
            this.toast.success('Email deleted successfully');
          }, 1500);
          this.refetch();
          this.selectedIds = [];
        },
        error: (e) => {
          console.error(e);
          this.toast.error('Error while deleting email');
          this.btnLoading = false;
        }
      });
    } else {
      this.api.deleteOutlookEmail(param).subscribe({
        next: () => {
          setTimeout(() => {
            this.btnLoading = false;
            this.modalService.dismissAll();
            this.toast.success('Email deleted successfully');
          }, 1500);
          this.refetch();
          this.selectedIds = [];
        },
        error: (e) => {
          console.error(e);
          this.toast.error('Error while deleting email');
          this.btnLoading = false;
        }
      });
    }
  }

  getInitials(name: string): string {
    if (!name) {
      return '';
    }

    // Split the name into parts
    const nameParts = name.trim().split(' ');

    // Get the first letter of the first two parts only
    const initials = nameParts
      .slice(0, 2) // Take only the first two parts
      .map(part => part[0]?.toUpperCase()) // Get the first letter of each part
      .join('');

    return initials;
  }


  addAccountRequest(error: any) {
    console.error(error?.error || error.message || 'An error occurred');
    this.changeAccountNotConnected.emit(true);
    return of(null);
  }

  private handleOutlookError(error: any) {
    const message = error?.error?.message || error?.message || 'Unknown error';
    console.error('Outlook API error:', message);

    if (message === 'Please Link your Mailbox') {
      return this.addAccountRequest(error);
    }

    return throwError(() => error);
  }

  private handleGmailError(error: any) {
    const message = error?.error?.message || error?.message || 'Unknown error';
    const code = error?.error?.code;
    console.error('Gmail API error:', message, code);
    if (message === 'Please Link your Gmail Mailbox') {
      return this.addAccountRequest(error);
    }
    // Lambda returns 403 + code for revoked refresh / missing Gmail scope (common the day after a good session)
    const gmailReauthCodes = [
      'GMAIL_INSUFFICIENT_SCOPE',
      'GMAIL_REFRESH_INVALID_GRANT',
      'GMAIL_MISSING_REFRESH',
      'GMAIL_REFRESH_FAILED',
      'GMAIL_TOKENINFO_FAILED',
      'GMAIL_AUTH_ERROR'
    ];
    const looksLikeGmailBody =
      error?.status === 403 && /gmail|google/i.test(String(message));
    if (gmailReauthCodes.includes(String(code)) || looksLikeGmailBody) {
      this.toast.error(message, 'Gmail');
      this.changeAccountNotConnected.emit(true);
      return of(null);
    }
    return throwError(() => error);
  }
}

